import { Component, OnInit, HostListener, AfterViewInit, Inject, Injector, OnChanges, SimpleChanges } from '@angular/core';
import { DOCUMENT, formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ToxicGasEditorComponent } from './editor/toxic-gas-editor.component';
import { ToxicGasArchiveComponent } from './archive/toxic-gase-archive.component';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ToxicGas } from './toxic-gas';
import { ToxicGasService } from './toxic-gas.service';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { Observable, Subscription } from 'rxjs';
import { ToxicGasStateModel } from './store/toxic-gas/toxic-gas.state';
import { ActionPendingCatalog } from 'src/app/common/enumerations/enumerations';
import { HomePendingField } from '../home-pending-change/home-pending-change';

@Component({
  selector: 'app-toxic-gas-panel',
  templateUrl: './toxic-gas-panel.component.html',
  styleUrls: ['./toxic-gas-panel.component.scss']
})
export class ToxicGasPanelComponent extends BaseComponent implements OnInit, AfterViewInit {

  maxHeight: number | null = null;
  loading = false;
  actives: ToxicGas[] = [];

  toxicGases?: ToxicGas[];
  toxicGasesFiltered?: ToxicGas[];
  toxicGases$!: Observable<ToxicGasStateModel>;
  toxicGasesSubs!: Subscription;

  private resizeTimer: any;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public toxicGasService: ToxicGasService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  showArchive() {
    const self = this;
    const modal = this.dialog.open(ToxicGasArchiveComponent, { maxHeight: '500px' });
    modal.componentInstance.copyItemEvent.subscribe((itemToCopy: ToxicGas) => {
      itemToCopy.id = 0;
      self.showEdit(itemToCopy);
    });
  }

  showAdd() {
    this.showEdit({
      id: 0,
      name: '',
      description: '',
      date: new Date(),
      roofAccess: true,
      responsibleParty: '',
      createdByUser: this.getCurrentUser()?.name,
    });
  }

  showEdit(item: ToxicGas): void {
    const modal = this.dialog.open(ToxicGasEditorComponent, { width: '500px' });
    modal.componentInstance.dataItem = item;

    modal.componentInstance.returnedItemEvent.subscribe((returnedItem: ToxicGas) => {
      this.handleReturnedItem(returnedItem);
    });

    this.closeAllMenus();
  }

  /**
   * Shows the saved item with its pending badge; the change only reaches the Home page once approved.
   * @param returnedItem The live item returned by the API, including its pending request.
   */
  private handleReturnedItem(returnedItem: ToxicGas): void {
    this.loading = true;

    // Replace an edited item where it is so its card keeps its position; only new items are appended.
    const exists = this.actives.some(item => item.id === returnedItem.id);
    const updatedActives = exists
      ? this.actives.map(item => item.id === returnedItem.id ? returnedItem : item)
      : this.actives.concat(returnedItem);

    this.actives = updatedActives.map(i => ({
      ...i,
      createdByInitials: this.users.find(u => u.id === i.createdBy)?.initials || '',
      updatedByInitials: this.users.find(u => u.id === i.updatedBy)?.initials || '',
      proposedChanges: this.getProposedChanges(i)
    })).sort((a, b) => (a.roofAccess === b.roofAccess ? 0 : a.roofAccess ? 1 : -1));;
    // this.actives = this.toxicGasService.loadActives(this.actives);
    this.loading = false;
    this.alert.info('Your Toxic Gas change was submitted for approval.');
  }

  /**
   * Submits archiving for approval. The card stays with a pending badge; an unapproved new item is discarded right away.
   * @param id The Toxic Gas identifier.
   */
  archive(id: number) {
    this.closeAllMenus();
    this.toxicGasService.archive(id).toPromise().then(archiveItem => {
      // Archiving waits for secondary approval, so keep the card and show its pending request.
      if (archiveItem) {
        this.actives = this.actives.map(item => item.id === archiveItem.id ? { ...item, pendingChange: archiveItem.pendingChange, proposedChanges: [] } : item);
        this.alert.info('Archive submitted for approval.');
      }
      else
        this.actives = this.actives.filter(item => item.id !== id);
      // this.actives = this.toxicGasService.loadActives(this.actives);
    }).catch(() => {
      this.alert.defaultError();
    });
  }

  /**
   * Pending new items are hidden from the Home page except for their submitter and Toxic Gas approvers.
   * @param item The Toxic Gas item.
   */
  private isVisible(item: ToxicGas): boolean {
    if (item.status == 1) return true;
    return item.status == 2 && !!item.pendingChange && (this.hasPrivilege(this.privilegeEnum.ToxicGasApprove) || item.pendingChange.createdBy == this.currentUser?.id);
  }

  /**
   * Tooltip for the latest audit line: who submitted the latest approved change and when, then who approved it and when (the two times differ).
   * Falls back to the changed-by name for items that never went through approval.
   * @param changedBy Name of the user who created or last edited the item.
   * @param item The Toxic Gas item.
   */
  getAuditTooltip(changedBy: string | undefined, item: ToxicGas): string {
    if (!item.approvedByUser || !item.approvedOn) return changedBy ?? '';
    const submitted = item.submittedOn ? 'Submitted by ' + (item.submittedByUser ?? changedBy ?? '') + ' on ' + formatDate(item.submittedOn, 'yyyy-MM-dd h:mm a', 'en-US') + ' · ' : '';
    return submitted + 'Approved by ' + item.approvedByUser + ' on ' + formatDate(item.approvedOn, 'yyyy-MM-dd h:mm a', 'en-US');
  }

  /**
   * Lists the proposed values of a pending edit that differ from the approved ones.
   * @param item The Toxic Gas item with its pending request.
   */
  private getProposedChanges(item: ToxicGas): HomePendingField[] {
    const proposed = item.pendingChange?.proposed;
    if (!proposed || item.pendingChange?.action != ActionPendingCatalog.Update) return [];

    const changes: HomePendingField[] = [];
    if (proposed.description != item.description) changes.push({ label: 'Location', value: proposed.description });
    if (proposed.name != item.name) changes.push({ label: 'Gas', value: proposed.name });
    if (proposed.responsibleParty != item.responsibleParty) changes.push({ label: 'Responsible', value: proposed.responsibleParty ?? '' });
    if (proposed.roofAccess != item.roofAccess) changes.push({ label: 'Roof Access', value: proposed.roofAccess ? 'OK' : 'Not OK' });
    if (new Date(proposed.date).getTime() != new Date(item.date).getTime()) changes.push({ label: 'Date', value: new Date(proposed.date).toLocaleDateString() });
    return changes;
  }

  toggleMenu(i: number) {
    const element = this.document.getElementById('toxicgas-menu-' + i.toString());
    if (element) {
      element.classList.toggle('collapse');

      if (!element.classList.contains('collapse')) {
        const rect = element.parentElement?.getBoundingClientRect();
        element.style.top = (rect?.top ?? 0 + 30) + 'px;';
        element.style.left = (rect?.right ?? 0 - 40) + 'px;';
      }
    }
  }

  closeAllMenus() {
    this.document.querySelectorAll('div.toxicgas-menu:not(.collapse)')
      .forEach(menu => { menu.classList.add('collapse'); });
  }

  ngOnInit() {
    this.getUserTable().then(() => this.loadToxicGases());
  }

  getUserTable(): Promise<void> {
    return new Promise(resolve => {
      const checkUsers = setInterval(() => {
        this.users = super.getUsers();
        if (this.users && this.users.length > 0) {
          clearInterval(checkUsers);
          resolve();
        }
      }, 100); // Check every 100ms
    });
  }

  loadToxicGases() {
    this.loading = true;
    this.toxicGases$ = this.store.select(state => state.ToxicGas);
    this.toxicGasesSubs = this.toxicGases$.subscribe(state => {
      this.loading = state.loading;
      const data = state.data;
      if (data?.length) {
        this.toxicGases = data;
        this.actives = this.toxicGases
          .filter(t => this.isVisible(t))
          .sort((a, b) => (a.roofAccess === b.roofAccess ? 0 : a.roofAccess ? 1 : -1))
          .map(i => ({
            ...i,
            createdByInitials: this.users.find(u => u.id === i.createdBy)?.initials || '',
            updatedByInitials: this.users.find(u => u.id === i.updatedBy)?.initials || '',
            proposedChanges: this.getProposedChanges(i)
          }));

        this.loading = false;
      }
    });
  }

  refresh() {
    // this.responsiveFunction();
    this.loading = true;
    this.toxicGasService.getActive().toPromise().then(toxicGas => {
      if (toxicGas)
        this.actives = this.toxicGasService.loadActives(toxicGas);
      this.loading = false;
    }).catch(() => {
      this.alert.defaultError();

      ;
    });
  }

  ngAfterViewInit() {
    // this.responsiveFunction();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: { innerWidth: any; innerHeight: any; }; }) {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.innerWidth = event.target.innerWidth;
      this.innerHeight = event.target.innerHeight;
      this.maxHeight = parseInt((this.innerHeight * 0.45).toFixed(0), 10);
    }, 200);
  }
}
